import { useNavigate } from '@tanstack/react-router';
import { T, useGT } from 'gt-react';
import type { Row } from '@/lib/tanstack-table';
import type { AssignmentLine } from 'src/api/rpc/hooks/procurement/useReplenishmentSuggestions';
import { hasMergedDeficit } from '../utils/lineGroups';
import { useReplenishmentBasePath } from '../utils/useReplenishmentBasePath';
import { TroubleDataBadges } from '@/components/common/TroubleDataBadges';

export function TriggerCell({
  row,
  showPotf = false,
}: {
  row: Row<AssignmentLine>;
  /** Mark a POTF line; only needed when the POTF group header isn't shown. */
  showPotf?: boolean;
}) {
  const gt = useGT();
  const navigate = useNavigate();
  const basePath = useReplenishmentBasePath();
  const line = row.original;
  const isSalesOrder = line.trigger === 'sales_order';

  const triggerBadge = (() => {
    if (isSalesOrder && line.triggerSalesOrderId) {
      return (
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            void navigate({
              to: basePath,
              search: (previous: Record<string, unknown>) => ({
                ...previous,
                suggestionId: line.suggestionId,
              }),
              resetScroll: false,
            });
          }}
          className="rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium truncate text-red-700 hover:bg-red-200 cursor-pointer"
          title={gt('Open suggestion (sales order detail embedded)', {
            context: 'trigger badge so click tooltip',
          })}
        >
          {gt('SO', { context: 'trigger badge short' })}
        </button>
      );
    }
    if (line.trigger === 'manual') {
      const merged = hasMergedDeficit(line);
      return (
        <span
          className="rounded-full px-2 py-0.5 text-xs font-medium truncate inline-block bg-blue-100 text-blue-700"
          title={
            merged
              ? gt(
                  "A stock line a buyer added. The engine's deficit for this item merged into it; Calc is the engine's quantity, Qty the buyer's.",
                  {
                    context:
                      'trigger badge tooltip: buyer-added stock line that a later engine run merged its min/max deficit into',
                  },
                )
              : gt('A stock line a buyer added', {
                  context: 'trigger badge buyer-added stock line tooltip',
                })
          }
        >
          {merged
            ? gt('Added + Deficit', {
                context:
                  'trigger badge: buyer-added stock line with the engine deficit merged in',
              })
            : gt('Added', { context: 'trigger badge buyer-added stock line' })}
        </span>
      );
    }
    if (line.trigger === 'deficit') {
      return (
        <span className="rounded-full px-2 py-0.5 text-xs font-medium truncate inline-block bg-amber-100 text-amber-800">
          {gt('Deficit', { context: 'trigger badge deficit' })}
        </span>
      );
    }
    return (
      <span
        className={`rounded-full px-2 py-0.5 text-xs font-medium truncate inline-block ${
          isSalesOrder ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700'
        }`}
      >
        {isSalesOrder
          ? gt('SO', { context: 'trigger badge short' })
          : gt('Min/Max', { context: 'trigger badge' })}
      </span>
    );
  })();

  return (
    <div className="flex items-center gap-1 whitespace-nowrap [&>*]:shrink-0">
      {triggerBadge}
      <TroubleDataBadges troubleData={line.triggerSalesOrderTroubleData} />
      {showPotf && (
        <span
          className="rounded-full bg-violet-100 px-1.5 py-0.5 text-xs font-medium text-violet-700"
          title={gt('Non-stock / POTF line (popularity = N or blank)', {
            context: 'potf inline badge tooltip',
          })}
        >
          <T context="potf inline badge">POTF</T>
        </span>
      )}
    </div>
  );
}
